import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { PayloadAction } from '@reduxjs/toolkit';
import { Button } from 'antd';
import cs from 'classnames';

import { ReactComponent as ArrowIcon } from 'assets/icons/arrowDown.svg';

import styles from './SidebarNavigation.module.scss';

interface SidebarNavigationProps<T extends object> {
  id: string;
  data: T[];
  isContainerStyles?: boolean;
  action: (id: string) => PayloadAction<string>;
  getItemId?: (item: T) => string;
}

const getDefaultItemId = (item: object) => {
  if ('id' in item && typeof item.id === 'string') {
    return item.id;
  }

  return '';
};

const SidebarNavigation = <T extends object>({
  id,
  data,
  isContainerStyles = true,
  action,
  getItemId = getDefaultItemId
}: SidebarNavigationProps<T>) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  if (!data.length) {
    return null;
  }

  const handlePrevClick = () => {
    const currentIndex = data.findIndex((item) => getItemId(item) === id);
    if (currentIndex > 0) {
      const prevItem = data[currentIndex - 1];
      dispatch(action(getItemId(prevItem)));
    }
  };

  const handleNextClick = () => {
    const currentIndex = data.findIndex((item) => getItemId(item) === id);
    if (currentIndex < data.length - 1) {
      const nextItem = data[currentIndex + 1];
      dispatch(action(getItemId(nextItem)));
    }
  };

  const currentIndex = data.findIndex((item) => getItemId(item) === id);

  return (
    <div className={cs({ [styles.container]: isContainerStyles })}>
      <Button
        type="link"
        icon={<ArrowIcon />}
        onClick={handlePrevClick}
        disabled={currentIndex <= 0}
        className={cs(styles.button, styles.prevButton)}
      >
        {t('sidebar.nav.previous')}
      </Button>
      <Button
        type="link"
        icon={<ArrowIcon />}
        iconPosition="end"
        onClick={handleNextClick}
        disabled={currentIndex >= data.length - 1}
        className={cs(styles.button, styles.nextButton)}
      >
        {t('sidebar.nav.next')}
      </Button>
    </div>
  );
};

export default SidebarNavigation;
